import { ApiQueryName } from "@/models/api"
import { Meter } from "@/models/meter"
import type { HttpError } from "@/utils/api"
import { toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { CollectionProxy } from "spraypaint/lib-esm/proxies"

import type { SpraypaintQueryOptions, UseApiOptions } from "../types"
import { useRenameQueryResult } from "../utils"
import { createQueryKey } from "../utils/createQueryKey"

/** Max page size allowed by the API; requests for more return UnsupportedPageSize */
const METERS_PAGE_SIZE = 1000

const placeholderMeters: Meter[] = Array.from(
  { length: 3 },
  (_, i) => new Meter({ id: String(i + 1), meterCode: "", meterProvider: "" })
)

const chunkItems = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = []

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }

  return chunks
}

const fetchMetersPage = async ({
  extraFields,
  includes,
  page,
  pageSize,
  where,
}: SpraypaintQueryOptions): Promise<Meter[]> => {
  const response: CollectionProxy<Meter> = await Meter.where(where ?? {})
    .includes(includes ?? [])
    .page(page ?? 1)
    .per(pageSize ?? METERS_PAGE_SIZE)
    .selectExtra(extraFields ?? [])
    .all()

  return response.data
}

const getMeters = async ({
  extraFields,
  includes,
  page,
  pageSize,
  where,
}: SpraypaintQueryOptions): Promise<Meter[]> => {
  const effectivePageSize = Math.min(
    pageSize ?? METERS_PAGE_SIZE,
    METERS_PAGE_SIZE
  )
  const meterIds = where?.id

  try {
    // ID lookups can exceed the API page cap. Batch the filter so every
    // requested meter is fetched instead of dropping results after page one.
    if (Array.isArray(meterIds) && meterIds.length > effectivePageSize) {
      const pages = await Promise.all(
        chunkItems(meterIds, effectivePageSize).map((ids) =>
          fetchMetersPage({
            extraFields,
            includes,
            page: 1,
            pageSize: ids.length,
            where: { ...where, id: ids },
          })
        )
      )

      return pages.flat()
    }

    return await fetchMetersPage({
      extraFields,
      includes,
      page,
      pageSize: effectivePageSize,
      where,
    })
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Returns meters for the given organization.
 * Use for admin meters grid; sorting and filtering are done client-side.
 *
 * @example
 * const { metersData, metersIsFetching } = useMeters({
 *   apiQueryOptions: { where: { organization_id: activeOrganizationId } },
 * })
 */
export const useMeters = (
  options: UseApiOptions<SpraypaintQueryOptions, Meter[], HttpError> = {}
) => {
  const { apiQueryOptions = {}, queryOptions = {} } = options

  const queryKey = createQueryKey(ApiQueryName.Meters, "getMany", null, {
    apiQueryOptions,
  })

  const queryResult = useQuery({
    placeholderData: placeholderMeters,
    ...queryOptions,
    queryFn: () => getMeters(apiQueryOptions),
    queryKey,
    throwOnError: resolveQueryThrowOnError(queryOptions.throwOnError),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Meters)
}
